import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { dayKey } from './useHabits'

// Shared couple state: today's mood for each partner + the
// anniversary date (days-together counter). Low-churn, so it just
// fetches on mount and after a write — no realtime channel.
export function useCoupleExtras(partnerId) {
  const { user } = useAuth()
  const userId = user?.id || null

  const [myMood, setMyMood] = useState(null)
  const [partnerMood, setPartnerMood] = useState(null)
  const [anniversary, setAnniversary] = useState(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!userId) return
    let alive = true
    ;(async () => {
      const today = dayKey()
      const [{ data: moods }, { data: meta }] = await Promise.all([
        supabase.from('couple_moods').select('user_id, emoji').eq('day', today),
        supabase.from('couple_meta').select('anniversary_date').eq('id', 'global').maybeSingle(),
      ])
      if (!alive) return
      setMyMood((moods || []).find((m) => m.user_id === userId)?.emoji || null)
      setPartnerMood(partnerId ? ((moods || []).find((m) => m.user_id === partnerId)?.emoji || null) : null)
      setAnniversary(meta?.anniversary_date || null)
    })()
    return () => { alive = false }
  }, [userId, partnerId, tick])

  const setMood = useCallback(async (emoji) => {
    if (!userId || !emoji) return
    setMyMood(emoji) // optimistic
    await supabase.from('couple_moods').upsert(
      { user_id: userId, day: dayKey(), emoji },
      { onConflict: 'user_id,day' },
    )
    setTick((t) => t + 1)
  }, [userId])

  const setAnniversaryDate = useCallback(async (date) => {
    if (!userId || !date) return
    setAnniversary(date)
    await supabase.from('couple_meta').upsert(
      { id: 'global', anniversary_date: date, updated_by: userId, updated_at: new Date().toISOString() },
      { onConflict: 'id' },
    )
    setTick((t) => t + 1)
  }, [userId])

  const daysTogether = useMemo(() => {
    if (!anniversary) return null
    try {
      const start = new Date(anniversary + 'T00:00:00')
      const now = new Date()
      const diff = Math.floor((now - start) / 86400000)
      return diff >= 0 ? diff : null
    } catch { return null }
  }, [anniversary])

  return { myMood, partnerMood, anniversary, daysTogether, setMood, setAnniversaryDate }
}
